import { Badge, List, Popover } from "antd";
import { Icon, Title, Button, notification } from "components/common";
import { useEffect, useMemo, useState } from "react";
import {
  useLazyGetNotificationsQuery,
  useMarkAsReadNotificationMutation,
  useMarkAllAsReadNotificationMutation,
} from "features/shared/api";
import theme from "utils/themeVars";
import { useQueryUpdate } from "hooks";
import { useTranslation } from "react-i18next";
import NotificationTile from "./notificationTile";
import { useSelector } from "react-redux";

const Notifications = ({ onClick, app }) => {
  const { t } = useTranslation();
  const [getNotifications, { data, isFetching }] =
    useLazyGetNotificationsQuery();
  const [markRead] = useMarkAsReadNotificationMutation();
  const [markAllRead] = useMarkAllAsReadNotificationMutation();
  const [update] = useQueryUpdate();
  const userApps = useSelector(state => state.app.user?.info?.user?.apps)
  const selectedAppID = userApps?.find(appItem => appItem?.slug === app)?.id

  useEffect(() => {
    selectedAppID && getNotifications({ page: 1, app_id: selectedAppID });
  }, [selectedAppID]);

  const allNotificationsList = useMemo(() => {
    if (!data) return [];
    const list = [];
    if (data?.today_notifications?.length > 0) {
      list.push(...data.today_notifications);
    }
    if (data?.yesterday_notifications?.length > 0) {
      list.push(...data.yesterday_notifications);
    }
    if (data?.older_notifications?.length > 0) {
      list.push(...data.older_notifications);
    }
    return list;
  }, [data]);

  const onClickSingleRead = (item) => {
    if (item.is_read === false) {
      markRead(item.id)
        .unwrap()
        .then((response) => {
          if (response) {
            update("getNotifications", (draft) => {
              const updateNotification = (notifications) => {
                return notifications.map((not) =>
                  not.id === item.id ? { ...not, is_read: true } : not
                );
              };
              draft.today_notifications = updateNotification(
                draft.today_notifications || []
              );
              draft.yesterday_notifications = updateNotification(
                draft.yesterday_notifications || []
              );
              draft.older_notifications = updateNotification(
                draft.older_notifications || []
              );
              draft.unread_count = Math.max(0, (draft.unread_count || 0) - 1);
            }, null, {});
          }
        })
        .catch((error) => {
          notification.error({ message: error?.message || "Failed to mark notification as read" });
        });
    }
  };

  const onClickAllRead = () => {
    const unreadCount = allNotificationsList.filter(
      (e) => e.is_read === false
    ).length;

    if (unreadCount > 0) {
      markAllRead()
        .unwrap()
        .then(() => {
          update("getNotifications", (draft) => {
            const markAllNotificationsAsRead = (notifications) => {
              return notifications.map((not) => ({
                ...not,
                is_read: true,
              }));
            };
            draft.today_notifications = markAllNotificationsAsRead(
              draft.today_notifications || []
            );
            draft.yesterday_notifications = markAllNotificationsAsRead(
              draft.yesterday_notifications || []
            );
            draft.older_notifications = markAllNotificationsAsRead(
              draft.older_notifications || []
            );
            draft.unread_count = 0;
          }, null, {});
        })
        .catch((error) => {
          notification.error({ message: error?.message || "Failed to mark all notifications as read" });
        });
    }
  };

  const onLoadMore = () => {
    const nextPage = data?.pagination?.next_page;
    if (nextPage) {
      getNotifications({ page: nextPage, app_id: selectedAppID });
    }
  };

  const loadMore = useMemo(
    () =>
      !!data?.pagination?.next_page ? (
        <div
          style={{
            textAlign: "center",
            marginTop: 12,
            height: 32,
            lineHeight: "32px",
          }}
        >
          <Button
            type="link"
            onClick={onLoadMore}
            loading={isFetching}
            disabled={isFetching}
          >
            {t("View More")}
          </Button>
        </div>
      ) : null,
    [data?.pagination, isFetching],
  );

  return (
    <Popover
      arrowPointAtCenter
      placement="bottomRight"
      content={
        <div style={{ overflowY: "auto", maxHeight: 500, width: 500 }}>
          <List
            loadMore={loadMore}
            loading={isFetching}
            itemLayout="horizontal"
            dataSource={allNotificationsList}
            renderItem={(item, index) => (
              <NotificationTile
                key={item.id || index}
                item={item}
                onMarkAsRead={onClickSingleRead}
              />
            )}
          />
        </div>
      }
      title={
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Title level={4}>{t("Notifications")}</Title>
          <Button type="link" onClick={onClickAllRead}>
            {t("Mark all as read")}
          </Button>
        </div>
      }
      trigger="click"
      getPopupContainer={(node) => node.parentNode}
    >
      <Badge
        count={data?.unread_count}
        size="small"
        overflowCount={99}
        offset={[4, 0]}
        style={{ "--ant-padding-xs": "4px" }}
      >
        <Icon
          className="pointer"
          icon="IoIosNotifications"
          size={22}
          style={{ color: theme["primary-color"] }}
          onClick={onClick}
        />
      </Badge>
    </Popover>
  );
};

export default Notifications;
